import { Fragment, useState } from 'react';
import Collapsible from 'react-collapsible';
import { QRCodeCanvas } from 'qrcode.react';
import { BsCaretDownFill, BsCaretUpFill } from 'react-icons/bs';
import styled from 'styled-components';

import { DetailLink } from 'components/DetailsView';
import { FreefoodIcon } from 'components/FreefoodIcon';
import { DetailValue } from 'components/DetailsView/DetailValue';
import { StateHistory } from 'components/DetailsView/StateHistory';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { StatusTag } from 'pages/Deliveries/components/StatusTag';
import { BUNDLE_TYPES_LABELS } from 'pages/Bundles/constants/types';

import { DeliveryState } from 'data/__generated__';
import { formatDurationMinutes } from 'helpers/dateTimes';

import {
    deliveryHref,
    deliveryLabel,
    deliveryMetaLabel,
    deliveryWindow,
    formatWindow,
    getStopState,
    hasFreefood,
} from '../helpers';
import { WindowChip } from '../components/StopStatus';
import { IssueTag } from '../components/TourStatusTag';
import {
    DELIVERY_CLOSURE_PHOTO_TYPE,
    boxScans,
    formatHour,
    dueScanSteps,
    type DetailDelivery,
    type DetailTour,
} from './helpers';

/** Beyond half an hour the closure is late enough that ops wants to know why; below, it is road noise. */
const DELAY_THRESHOLD_MINUTES = 30;

const closurePhotos = (delivery: DetailDelivery) =>
    delivery.controlPhotos.filter(({ type }) => type === DELIVERY_CLOSURE_PHOTO_TYPE);

/**
 * What the manager has to go and look at on this delivery. The tour's own state gates the box ones: before the
 * departure a box without `loadedAt` is simply not loaded yet, and before the return an empty `returnedAt`
 * is a truck on the road, not a loss.
 *
 * A closure forced by the driver is NOT read from `Delivery.forced`: that field is Urbantz's (a manually
 * declared arrival) and is null on every delivery no closure ever stamped. « Sans photo » already carries
 * the same fact for our own closures — a delivered delivery with no proof.
 */
export const deliveryIssues = (delivery: DetailDelivery, tour: DetailTour): string[] => {
    const issues: string[] = [];
    // Not a stop `finish-all-deliveries` forced to delivered: it has no closure to check.
    const isDelivered =
        delivery.state === DeliveryState.Delivered && getStopState(delivery) === DeliveryState.Delivered;

    if (isDelivered && !closurePhotos(delivery).length) issues.push('Sans photo');
    if (isDelivered && !delivery.times.actualSlot.start) issues.push('Sans heure d’arrivée');
    if (delivery.delay != null && delivery.delay > DELAY_THRESHOLD_MINUTES) {
        issues.push(`+${formatDurationMinutes(delivery.delay)} hors créneau`);
    }

    const due = dueScanSteps(tour, delivery);
    const noDeparture = due.loadedAt ? delivery.boxes.filter((box) => box.loadedAt === null).length : 0;
    if (noDeparture) issues.push(`${noDeparture} caisse${noDeparture > 1 ? 's' : ''} sans scan départ`);

    // Once delivered, the rangement is done: a box never scanned on site was shelved without the check.
    const noSetupScan = due.setupScannedAt ? delivery.boxes.filter((box) => box.setupScannedAt == null).length : 0;
    if (noSetupScan) issues.push(`${noSetupScan} caisse${noSetupScan > 1 ? 's' : ''} sans scan rangement`);

    const noReturn = due.returnedAt ? delivery.boxes.filter((box) => box.returnedAt === null).length : 0;
    if (noReturn) issues.push(`${noReturn} caisse${noReturn > 1 ? 's' : ''} sans scan retour`);

    return issues;
};

/**
 * One delivery of the tour: a line that names it and flags what to check, and a dropdown holding the delivery's
 * details and its boxes under their column — QR code, flux, départ, rangement, retour (each scan with who did it).
 *
 * OPEN BY DEFAULT: the page is read to control a whole tour in one pass, so the day must be legible by
 * scrolling, never by clicking twenty deliveries open. The fold stays, to put aside what has been checked.
 */
export const DeliveryCard = ({
    delivery,
    sequence,
    tour,
}: {
    delivery: DetailDelivery;
    sequence: number;
    tour: DetailTour;
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const issues = deliveryIssues(delivery, tour);
    const { start, stop } = deliveryWindow(delivery);

    return (
        <Collapsible
            transitionTime={150}
            overflowWhenOpen="visible"
            open={isOpen}
            trigger={
                <DeliveryHeader onClick={() => setIsOpen((open) => !open)}>
                    <Caret>{isOpen ? <BsCaretUpFill size={13} /> : <BsCaretDownFill size={13} />}</Caret>
                    <Title>
                        {sequence}. {deliveryLabel(delivery)}
                    </Title>
                    {hasFreefood(delivery) && <FreefoodIcon />}
                    <WindowChip $muted={!start && !stop}>{formatWindow(start, stop)}</WindowChip>
                    <StatusTag state={getStopState(delivery)} />
                    {issues.map((issue) => (
                        <IssueTag key={issue} label={issue} />
                    ))}
                </DeliveryHeader>
            }
        >
            <DeliveryDetails delivery={delivery} />
        </Collapsible>
    );
};

export const DeliveryDetails = ({ delivery }: { delivery: DetailDelivery }) => (
    <Details>
        <DetailsColumn>
            <DetailValue label="Site" value={deliveryMetaLabel(delivery)} />
            <DetailValue
                label="Temps sur place"
                sublabel={`estimé ${formatDurationMinutes(delivery.theoricServiceTime ?? 0)}`}
                value={delivery.serviceTime != null ? formatDurationMinutes(delivery.serviceTime) : '—'}
            />
            <DetailValue
                label="Arrivée → clôture"
                value={`${formatHour(delivery.times.actualSlot.start)} → ${formatHour(delivery.times.actualSlot.end)}`}
            />
            {delivery.driversComment ? (
                <DetailValue label="Commentaire du livreur" value={delivery.driversComment} />
            ) : null}
            <DetailLink label="Fiche livraison" to={deliveryHref(delivery._id)} name={delivery.label} />
            <StateHistory
                label="Étapes de la livraison"
                records={delivery.stateHistory.map((record) => ({
                    ...record,
                    state: <StatusTag state={record.state} />,
                }))}
            />
        </DetailsColumn>
        <DetailsColumn>
            <Label>Caisses ({delivery.boxes.length})</Label>
            {delivery.boxes.length ? (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Caisse</TableHead>
                            <TableHead>Flux</TableHead>
                            <TableHead>Contrôle départ</TableHead>
                            <TableHead>Chargement</TableHead>
                            <TableHead>Rangement</TableHead>
                            <TableHead>Retour</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {[...delivery.boxes]
                            .sort((first, second) => first.number - second.number)
                            .map((box, index, boxes) => {
                                const { check, departure, setup, back } = boxScans(box);
                                const isNewColumn = index === 0 || box.column?._id !== boxes[index - 1].column?._id;
                                return (
                                    <Fragment key={box._id}>
                                        {isNewColumn && (
                                            <TableRow className="border-t border-b-0">
                                                <ColumnHeader colSpan={6}>
                                                    {box.column?.name ?? 'Sans colonne'}
                                                </ColumnHeader>
                                            </TableRow>
                                        )}
                                        <TableRow className="border-b-0">
                                            <TableCell>
                                                <BoxCell>
                                                    <QRCodeCanvas value={box.deliveryCode} size={44} />
                                                    <span>
                                                        #{box.number} {box.deliveryCode}
                                                    </span>
                                                </BoxCell>
                                            </TableCell>
                                            <TableCell>{BUNDLE_TYPES_LABELS[box.type]}</TableCell>
                                            <Scan>{check}</Scan>
                                            <Scan>{departure}</Scan>
                                            <Scan>{setup}</Scan>
                                            <Scan>{back}</Scan>
                                        </TableRow>
                                    </Fragment>
                                );
                            })}
                    </TableBody>
                </Table>
            ) : (
                <Empty>Aucune caisse sur cet arrêt</Empty>
            )}
        </DetailsColumn>
    </Details>
);

export const DeliveryHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    cursor: pointer;
    padding: 6px 0;
`;

export const Caret = styled.span`
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.infoTextColor};
`;

export const Title = styled.span`
    font-size: 16px;
    font-weight: 700;
    color: ${({ theme }) => theme.textColor};
`;

// The dropdown: the delivery on the left, its boxes on the right.
export const Details = styled.div`
    margin: 5px 0 15px 22px;
    display: flex;
    gap: 30px;
`;

export const DetailsColumn = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const Label = styled.span`
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.infoTextColor};
`;

const BoxCell = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-variant-numeric: tabular-nums;
`;

const Scan = styled(TableCell)`
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

const ColumnHeader = styled(TableCell)`
    font-weight: 700;
    color: ${({ theme }) => theme.textColor};
`;

export const Empty = styled.span`
    font-size: 13px;
    color: ${({ theme }) => theme.infoTextColor};
`;
